import React, { useCallback, useEffect, useState } from "react";
import { LoaderCircle, Play, Square } from "lucide-react";
import {
  fetchReassignmentDiagnosticsHistoryConfig,
  updateReassignmentDiagnosticsHistoryConfig,
} from "../lib/systemConfigApi.js";
import {
  buttonSizeClasses,
  buttonToneClasses,
  joinClasses,
  pillToneClasses,
  statusToneClasses,
} from "./resultsPrimitives/shared.js";

const PRIMARY_ACTION_CLASS_NAME = joinClasses([
  "inline-flex items-center justify-center gap-2 rounded-[4px] border font-semibold transition disabled:cursor-not-allowed disabled:opacity-60",
  buttonSizeClasses.sm,
  "border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong focus-visible:ring-2 focus-visible:ring-accent",
]);
const SECONDARY_ACTION_CLASS_NAME = joinClasses([
  "inline-flex items-center justify-center gap-2 rounded-[4px] border font-semibold transition disabled:cursor-not-allowed disabled:opacity-60",
  buttonSizeClasses.sm,
  buttonToneClasses.ghost,
]);
const DANGER_ACTION_CLASS_NAME = joinClasses([
  "inline-flex items-center justify-center gap-2 rounded-[4px] border font-semibold transition disabled:cursor-not-allowed disabled:opacity-60",
  buttonSizeClasses.sm,
  "border-rose-200 bg-white text-rose-700 hover:border-rose-300 hover:bg-rose-50",
]);

export default function ReassignmentDiagnosticsSettingsCard() {
  const [config, setConfig] = useState(null);
  const [isConfigLoading, setIsConfigLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [configError, setConfigError] = useState("");
  const [feedback, setFeedback] = useState(null);

  const loadConfig = useCallback(async () => {
    setIsConfigLoading(true);
    setConfigError("");
    try {
      const payload = await fetchReassignmentDiagnosticsHistoryConfig();
      setConfig({ is_active: payload?.is_active === true });
      return payload;
    } catch (error) {
      setConfig(null);
      setConfigError(error instanceof Error ? error.message : "Impossible de charger la configuration.");
      return null;
    } finally {
      setIsConfigLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  const handleStart = useCallback(async () => {
    if (config?.is_active || isUpdating) {
      return;
    }

    setIsUpdating(true);
    setFeedback(null);
    try {
      const nextConfig = await updateReassignmentDiagnosticsHistoryConfig({ is_active: true });
      setConfig({ is_active: nextConfig?.is_active === true });
      setFeedback({
        message: "La collecte des trois historiques est maintenant active.",
        tone: "success",
      });
    } catch (error) {
      setFeedback({
        message: error instanceof Error ? error.message : "Impossible d’activer le cumul.",
        tone: "danger",
      });
    } finally {
      setIsUpdating(false);
    }
  }, [config?.is_active, isUpdating]);

  const handleStop = useCallback(async () => {
    if (!config?.is_active || isUpdating) {
      return;
    }

    setIsUpdating(true);
    setFeedback(null);
    try {
      const nextConfig = await updateReassignmentDiagnosticsHistoryConfig({ is_active: false });
      setConfig({ is_active: nextConfig?.is_active === true });
      setFeedback({
        message: "Le cumul est arrêté. L’historique déjà enregistré reste disponible.",
        tone: "info",
      });
    } catch (error) {
      setFeedback({
        message: error instanceof Error ? error.message : "Impossible d’arrêter le cumul.",
        tone: "danger",
      });
    } finally {
      setIsUpdating(false);
    }
  }, [config?.is_active, isUpdating]);

  const isActive = !configError && (config?.is_active ?? true) === true;
  const canToggle = Boolean(config) && !isConfigLoading && !isUpdating;
  const feedbackClassName = feedback?.tone === "danger"
    ? statusToneClasses.danger
    : feedback?.tone === "success"
      ? statusToneClasses.success
      : statusToneClasses.info;

  return (
    <article
      className="grid gap-2.5 rounded-[4px] border border-black/6 bg-slate-50/75 px-3.5 py-3 shadow-[0_10px_24px_rgba(15,23,42,0.04)]"
      data-reassignment-diagnostics-settings="true"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="grid max-w-4xl gap-0.5">
          <div className="flex flex-wrap items-center gap-2">
            <p className="m-0 text-[0.94rem] font-semibold tracking-[-0.02em] text-slate-950">Collecte des activités</p>
            <span className={joinClasses([
              "inline-flex items-center rounded-[4px] px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-[0.12em]",
              isActive ? pillToneClasses.success : pillToneClasses.danger,
            ])}>
              {isActive ? "Actif" : "Inactif"}
            </span>
          </div>
          <p className="m-0 text-[0.8rem] leading-5 text-slate-600">
            Pilote Activité Dispatch, Activités Mise à jour, Activités Consolidation, Imports commandes et Rapports. Les entrées enregistrées restent disponibles après l’arrêt.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          {isActive ? (
            <button className={DANGER_ACTION_CLASS_NAME} disabled={!canToggle} onClick={() => void handleStop()} type="button">
              {isUpdating ? <LoaderCircle className="animate-spin" size={15} aria-hidden="true" /> : <Square size={14} aria-hidden="true" />}
              Arrêter
            </button>
          ) : (
            <button
              aria-pressed="false"
              className={PRIMARY_ACTION_CLASS_NAME}
              disabled={!canToggle}
              onClick={() => void handleStart()}
              type="button"
            >
              {isUpdating ? <LoaderCircle className="animate-spin" size={15} aria-hidden="true" /> : <Play size={15} aria-hidden="true" />}
              Démarrer
            </button>
          )}
        </div>
      </div>

      {isConfigLoading ? (
        <div className="flex items-center gap-2 rounded-[4px] border border-slate-200 bg-white px-3 py-2.5 text-[0.78rem] font-medium text-slate-600">
          <LoaderCircle className="animate-spin" size={15} aria-hidden="true" />
          Chargement de la configuration…
        </div>
      ) : configError ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-[4px] border border-rose-200 bg-rose-50 px-3 py-2 text-[0.78rem] font-medium text-rose-700" role="alert">
          <span>{configError}</span>
          <button className={SECONDARY_ACTION_CLASS_NAME} onClick={() => void loadConfig()} type="button">Réessayer</button>
        </div>
      ) : null}

      {feedback ? (
        <p className={joinClasses(["m-0 rounded-[4px] px-3 py-2 text-[0.78rem] font-medium", feedbackClassName])} role="status">
          {feedback.message}
        </p>
      ) : null}
    </article>
  );
}
